import { Injectable } from '@angular/core';
import { DataEntity } from 'octopus-connect';
import { CommunicationCenterService } from '@modules/communication-center';
import { AuthenticationService } from '@modules/authentication';
import { CorpusService } from '../corpus.service'; // Adjust path if needed
import { DynamicNavigationService } from '../../../../navigation/dynamic-navigation.service'; // Adjust path if needed
import { FuseNavigationItem } from 'fuse-core/types';
import { merge } from 'lodash-es';
import { Subscription } from 'rxjs';
import { modulesSettings } from '../../../../settings'; // Adjust path if needed
import { ModelSchema, Structures } from 'octopus-model';

// Reuse settings structure definition or import if defined elsewhere
const settingsStructure: ModelSchema = new ModelSchema({
    accessMatrix: Structures.object(),
});

@Injectable({
    providedIn: 'root'
})
export class CorpusMenuService {

    private isMenuSet = false;
    private authSubscription: Subscription | null = null;
    private settings: { [key: string]: any };

    constructor(
        private communicationCenter: CommunicationCenterService,
        private authService: AuthenticationService,
        private corpusService: CorpusService,
        private dynamicNavigation: DynamicNavigationService
    ) {
    }

    public initialize(): void {
        // Load settings similarly to how CorpusModule did
        // Ensure modulesSettings is accessible here
        const corpusModuleSettings = modulesSettings.corpus || {}; // Handle potential undefined settings
        this.settings = settingsStructure.filterModel(corpusModuleSettings);

        this.listenToAuthChanges();
    }

    private listenToAuthChanges(): void {
        // Unsubscribe from previous subscription if any
        this.authSubscription?.unsubscribe();

        this.authSubscription = this.communicationCenter
            .getRoom('authentication')
            .getSubject('userData')
            .subscribe((data: DataEntity | null) => {
                if (data) {
                    // User logged in
                    this.setupCorpusMenu();
                } else {
                    // User logged out
                    this.clearCorpusMenu();
                }
            });
    }

    private clearCorpusMenu(): void {
        if (!this.isMenuSet) {
            return; // Avoid clearing if not set
        }
        console.log('[CorpusMenuService] Clearing corpus menu items.');
        this.dynamicNavigation.clearMenuItem('level0', 'corpus');
        this.dynamicNavigation.clearMenuItem('level0', 'corpusPdf');
        this.dynamicNavigation.clearMenuItem('level0', 'corpus-global');
        this.dynamicNavigation.clearMenuItem('level0', 'corpus-user');
        this.dynamicNavigation.clearMenuItem('level0', 'corpus-community');
        this.isMenuSet = false;
    }

    private setupCorpusMenu(): void {
        // Logic moved from CorpusModule.postAuthentication
        // Ensure corpusService.settings and corpusService.currentUser are loaded
        // It might be safer to wait for corpusService to be ready if its data is async

        // Add a check to ensure corpusService properties are available
        if (!this.corpusService.settings || !this.corpusService.currentUser) {
             console.warn('[CorpusMenuService] CorpusService settings or currentUser not yet available. Menu setup deferred.');
             // Optionally, retry later or listen to an event from CorpusService
             return;
        }


        if (
            !this.isMenuSet &&
            this.corpusService.settings.globalCorpus &&
            this.settings && // Use settings loaded in this service's constructor
            this.authService.hasLevel(this.settings.accessMatrix?.['global']?.['view'] ?? []) // Added safe navigation
        ) {
            const corpusGlobal: FuseNavigationItem = {
                id: 'corpus-global',
                title: 'Ressources Lamorim', // Consider using translate keys
                translate: 'corpus.title.generic',
                type: 'item',
                icon: 'corpus', // Added icon for consistency
                url: '/corpus/' + this.corpusService.settings.globalCorpus,
            };

            const corpusUser: FuseNavigationItem = {
                id: 'corpus-user',
                title: 'Mes ressources', // Consider using translate keys
                translate: 'corpus.title.user',
                type: 'item',
                icon: 'corpus', // Added icon for consistency
                url: '/corpus/user/' + this.corpusService.currentUser.id,
            };

            const children: FuseNavigationItem[] = [
                corpusGlobal,
                corpusUser
            ];

            if (this.corpusService.settings.shareableResourceToCommunity) {
                 children.push({
                     id: 'corpus-community',
                     title: 'Ressources de la communauté', // Consider using translate keys
                     translate: 'corpus.corpus_community',
                     type: 'item',
                     icon: 'corpus', // Added icon for consistency
                     url: '/corpus/community/' + this.corpusService.settings.globalCorpus,
                 });
            }

            // Determine if it should be a collapsible item or a single item
            if (this.corpusService.settings.userCorpus && children.length > 1) {
                 // More than one item (Global + User +/- Community), make it collapsible
                 this.dynamicNavigation.registerModuleMenu('level0', {
                     id: 'corpus',
                     title: 'Corpus', // Consider using translate keys
                     translate: 'corpus.title',
                     type: 'collapse',
                     icon: 'corpus',
                     children: children,
                 });
            } else {
                 // Only Global Corpus or User Corpus exists, register as single item(s)
                 // Register Global Corpus if it exists
                 if (this.corpusService.settings.globalCorpus) {
                     this.dynamicNavigation.registerModuleMenu('level0', corpusGlobal);
                 }
                 // Register User Corpus if it exists (and different from global)
                 if (this.corpusService.settings.userCorpus && this.corpusService.currentUser?.id) {
                     // Avoid duplicate if user corpus IS the global corpus (unlikely but possible)
                     if(corpusGlobal.url !== corpusUser.url) {
                        this.dynamicNavigation.registerModuleMenu('level0', corpusUser);
                     }
                 }
                 // Register Community Corpus if applicable and not already covered
                 if (this.corpusService.settings.shareableResourceToCommunity) {
                    const communityItem = children.find(c => c.id === 'corpus-community');
                    // Removed check for existing item as getNavigationById does not exist
                    if (communityItem) {
                         this.dynamicNavigation.registerModuleMenu('level0', communityItem);
                    }
                 }
            }


            // Register PDF link if applicable (logic might need adjustment based on exact requirements)
            // This was registered separately in the original code.
            if (this.corpusService.settings.globalCorpus) { // Assuming PDF relates to global corpus
                this.dynamicNavigation.registerModuleMenu('level0', {
                    id: 'corpusPdf',
                    title: 'CorpusPdf', // Consider using translate keys
                    translate: 'corpus.title.pdf',
                    type: 'item',
                    icon: 'corpus_pdf',
                    url: '/corpus/pdf/' + this.corpusService.settings.globalCorpus,
                });
            }

            this.isMenuSet = true;
        } else {
             console.log('[CorpusMenuService] Conditions not met for setting up corpus menu.');
             if(this.isMenuSet) {
                 console.log('[CorpusMenuService] Clearing menu because conditions are no longer met.');
                 this.clearCorpusMenu(); // Clear if previously set but conditions no longer met
             }
        }
    }

    // Optional: Method to explicitly trigger menu setup if needed elsewhere
    // public initializeMenu(): void {
    //     this.setupCorpusMenu();
    // }

    // Optional: Cleanup logic if the service itself is ever destroyed (unlikely for root-provided)
    // ngOnDestroy(): void {
    //     this.authSubscription?.unsubscribe();
    // }
}